import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import * as React from 'react';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { getSimpleDate, getSimpleDateTodayTomorrow } from 'src/utils/date';
import { observer } from 'src/utils/mobx-react';
import {
    EventRowInPlayInfo,
    EventInPlayIcon,
    EventRowTimeInfo,
    EventStatus,
    BabIconWrapper,
} from 'src/domains/sportsbook/webview/components/events/row/StatusAndDate.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { DateTime } from 'src_common/utils/time/time';
import { useCommon } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useEventPhaseClockLabel } from 'src/domains/sportsbook/webview/components/events/row/useEventPhaseClockLabel';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

interface EventStatusAndDatePropsType {
    dateFormat: 'onlyToday' | 'todayAndTomorrow';
    marketType?: '6pack';
    smallEvent: EventListGroupEventItemType;
}

interface UnifiedInPlayPhaseStatusPropsType {
    eventModel: EventModel;
    variant: DefaultComponentVariantType | null;
    isRabAvailable: boolean;
    onIconClick: () => void;
}

const UnifiedInPlayPhaseStatus = observer('UnifiedInPlayPhaseStatus', (props: UnifiedInPlayPhaseStatusPropsType) => {
    const { eventModel, variant, isRabAvailable, onIconClick } = props;
    const common = useCommon();
    const geniusTimersEnabled = ConfigComponents.get(common).config.geniusTimersEnabled;
    const phaseClockLabel = useEventPhaseClockLabel(
        eventModel.timeElapsedInPhase,
        eventModel.currentPhase,
        eventModel.sport,
        geniusTimersEnabled
    );

    return (
        <EventStatus variant={variant}>
            {phaseClockLabel !== '' ? (
                phaseClockLabel
            ) : (
                <I18n
                    langKey='events.row.live'
                    defaultText='Live'
                />
            )}
            {isRabAvailable ? <BabIconWrapper onClick={onIconClick} /> : null}
        </EventStatus>
    );
});

export const EventStatusAndDate = observer('EventStatusAndDate', (props: EventStatusAndDatePropsType) => {
    const { smallEvent, dateFormat, marketType } = props;

    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const languagesState = LanguagesState.get(common);
    const unifiedComponentConfig = ConfigComponents.get(common).unifiedComponentConfig;
    const eventModel = smallEvent.id2.getEventModel();

    if (eventModel === null) {
        return <>{getSimpleDate(DateTime.from(smallEvent.startTime)?.toISOString() ?? 'time error', languagesState)}</>;
    }

    const isRabAvailable = appSportsBookState.rab.isAvailableForEvent(eventModel.id2);
    const onIconClick = (): void => {
        appSportsBookState.rab.onOpenForEvent(eventModel.id2);
    };

    if (eventModel.timeSettingsStarted) {
        if (marketType === '6pack') {
            return (
                <EventRowInPlayInfo>
                    <EventInPlayIcon />
                    <I18n
                        langKey='events.row.6pack.in-play'
                        defaultText='In-Play'
                    />
                    {isRabAvailable ? <BabIconWrapper onClick={onIconClick} /> : null}
                </EventRowInPlayInfo>
            );
        }

        if (unifiedComponentConfig.unifiedSportEventsTable) {
            return (
                <UnifiedInPlayPhaseStatus
                    eventModel={eventModel}
                    variant={unifiedComponentConfig.componentVariants.sportsbookVariant}
                    isRabAvailable={isRabAvailable}
                    onIconClick={onIconClick}
                />
            );
        }

        return (
            <EventStatus variant={unifiedComponentConfig.componentVariants.sportsbookVariant}>
                <I18n
                    langKey='events.row.in-play'
                    defaultText='In Play'
                />
                {isRabAvailable ? <BabIconWrapper onClick={onIconClick} /> : null}
            </EventStatus>
        );
    }

    if (dateFormat === 'todayAndTomorrow') {
        return (
            <EventRowTimeInfo>
                {getSimpleDateTodayTomorrow(eventModel.timeSettingsStartTime, languagesState)}
                {isRabAvailable ? <BabIconWrapper onClick={onIconClick} /> : null}
            </EventRowTimeInfo>
        );
    }

    return (
        <>
            {getSimpleDate(eventModel.timeSettingsStartTime, languagesState)}
            {isRabAvailable ? <BabIconWrapper onClick={onIconClick} /> : null}
        </>
    );
});
